import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Lock, Settings, Play } from 'lucide-react';
import { lmsApi } from '../services/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';

function Courses() {
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isStudent = user?.role === 'student';

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      const res = await lmsApi.getCourses();
      setCourses(res.data);
    } catch (error) {
      toast.error('Failed to load courses');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Courses</h1>
          <p className="text-gray-600 mt-1">
            {isStudent ? 'Browse and learn from available courses' : 'View and manage courses'}
          </p>
        </div>
        {!isStudent && (
          <button
            onClick={() => navigate('/lms/manage')}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Settings className="w-4 h-4 mr-2" />
            Manage LMS
          </button>
        )}
      </div>

      {courses.length === 0 ? (
        <div className="text-center py-16">
          <BookOpen className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-500">No courses available yet</h3>
          <p className="text-gray-400 mt-1">
            {isStudent ? 'Check back later for new courses' : 'Create your first course from Manage LMS'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course) => {
            const progress = isStudent && course.lesson_count > 0
              ? Math.round((course.completed_count / course.lesson_count) * 100)
              : null;

            return (
              <div
                key={course.id}
                onClick={() => navigate(`/lms/courses/${course.id}`)}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
              >
                {course.thumbnail_url ? (
                  <img
                    src={course.thumbnail_url}
                    alt={course.title}
                    className="w-full h-40 object-cover"
                  />
                ) : (
                  <div className="w-full h-40 bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center">
                    <Play className="w-12 h-12 text-white" />
                  </div>
                )}

                <div className="p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    {course.levels?.map(level => (
                      <span
                        key={level}
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          level === 'L1' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {level}
                      </span>
                    ))}
                    {!isStudent && !course.is_published && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-medium">
                        Draft
                      </span>
                    )}
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900">{course.title}</h3>
                  {course.description && (
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{course.description}</p>
                  )}

                  <div className="flex items-center justify-between mt-3">
                    <span className="text-sm text-gray-400">{course.lesson_count} lesson{course.lesson_count !== 1 ? 's' : ''}</span>
                    {course.creator_name && (
                      <span className="text-sm text-gray-400">by {course.creator_name}</span>
                    )}
                  </div>

                  {progress !== null && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                        <span>Progress</span>
                        <span>{progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-primary-600 h-2 rounded-full transition-all"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Courses;
